import React, { useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableHeader,
} from "../../design/table";
import { FinancialItem } from "../../models/FinancialItem";
import FinancialItemListItem from "./FinancialListItem";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { Subheading } from "../../design/heading";
import { useCreateFinancialItemDialog } from "../../providers/CreateFinancialItemDialogProvider/CreateFinancialItemDialogProvider";
import { Button } from "../../design/button";
import { FinancialItemTypeData } from "../../models/FinancialItemType";

interface Props {
  type: FinancialItemTypeData;
  items: FinancialItem[];
}

const FinancialItemListSection: React.FC<Props> = ({ type, items }) => {
  const { openDialog } = useCreateFinancialItemDialog();
  const [isOpen, setIsOpen] = useState(true);

  if (items.length === 0) return null;

  const totalValue = items.reduce((sum, item) => {
    const amount = item.category === "asset" ? item.amount : item.amount * -1;
    return sum + amount;
  }, 0);

  return (
    <div className="mb-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900">
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDownIcon className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronRightIcon className="w-5 h-5 text-muted-foreground" />
          )}
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {type.title}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {type.description}
            </p>
          </div>
        </div>
        <Subheading>
          <span className={totalValue < 0 ? "text-red-500" : ""}>
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(totalValue)}
          </span>
        </Subheading>
      </div>

      {/* Content */}
      {isOpen && (
        <div className="px-6 pb-6">
          <Table className="w-full table-fixed">
            <TableHead>
              <TableRow>
                <TableHeader className="w-1/6">Name</TableHeader>
                <TableHeader className="w-1/12" />
                <TableHeader className="w-1/6">Value</TableHeader>
                <TableHeader className="w-1/6">Debt</TableHeader>
                <TableHeader className="w-1/6">Equity</TableHeader>
                <TableHeader className="w-1/6" />
                <TableHeader className="w-1/12" />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((financialItem) => (
                <FinancialItemListItem
                  key={financialItem._id}
                  financialItem={financialItem}
                />
              ))}
            </TableBody>
          </Table>

          <div className="flex justify-end mt-6">
            <Button
              className="flex items-center px-4 py-2"
              onClick={() => openDialog(type.category, type.id)}
            >
              + Add {type.title.charAt(0).toUpperCase() + type.title.slice(1)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialItemListSection;
